import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import { getPortalSectionsAPI } from "../../../services/api";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const { sectionId, sectionItemTitle } = useParams();
  const { pathname } = useLocation();
  const { setExtraCrumbs } = useBreadcrumb();

  const fetchTransparencySections = async () => {
    setLoading(true);
    setExtraCrumbs(null);
    try {
      const response = await getPortalSectionsAPI();
      if (response?.status !== 200) return;

      const { data } = response;
      const section = data.find(s => s.id === Number(sectionId));

      let docs;
      if (section?.items) {
        docs = section.items.find(item => item.title === sectionItemTitle)?.documents;
      } else {
        const pair = data
          .flatMap(s => s.items ?? [])
          .flatMap(item => (item.subItems ?? []).map(sub => ({ parent: item, sub })))
          .find(({ sub }) => sub.id === Number(sectionId));

        if (pair) {
          docs = pair.sub.documents;
          setExtraCrumbs({
            forPath: pathname,
            crumbs: [
              { label: "Portal da Transparência", path: "/transparencia" },
              { label: pair.parent.title, path: `/transparencia/${pair.parent.path}` },
              { label: pair.sub.title },
            ],
          });
        }
      }

      if (docs?.length) {
        setDocuments(orderDocumentsByDate(docs));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const orderDocumentsByDate = docs => docs.sort((a, b) => new Date(b.date) - new Date(a.date));

  useEffect(() => {
    fetchTransparencySections();
  }, []);

  const normalized = searchTerm.toLowerCase();
  const filtered = documents.filter(doc =>
    doc.title.toLowerCase().includes(normalized) ||
    doc.description?.toLowerCase().includes(normalized)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSize = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  if (loading) {
    return <p className="py-8 text-center text-gray-500">Carregando...</p>;
  }

  return (
    <div className="p-4 space-y-6">
      {/* Search + per-page controls */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Pesquisar documentos..."
            className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-sm text-gray-600 whitespace-nowrap">Itens por página:</label>
          <select
            value={pageSize}
            onChange={handlePageSize}
            className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        {filtered.length === 0
          ? "Nenhum documento encontrado."
          : `Exibindo ${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, filtered.length)} de ${filtered.length} documento${filtered.length !== 1 ? "s" : ""}`
        }
      </p>

      {/* Document list */}
      {paginated.length === 0 && searchTerm ? (
        <p className="text-gray-500 text-center py-8">Nenhum resultado para "{searchTerm}".</p>
      ) : (
        paginated.map((doc) => (
          <div
            key={doc.id}
            className="border border-gray-300 rounded-2xl shadow-md"
          >
            <div className="p-4">
              <h2
                className="text-lg font-bold cursor-pointer hover:text-blue-700 transition"
                onClick={() => window.open(doc.fileUrl, "_blank")}
              >
                {doc.title}
              </h2>
              <p className="mt-1"><strong>Descrição:</strong> {doc.description}</p>
              <p><strong>Data:</strong> {new Date(doc.date).toLocaleDateString("pt-BR")}</p>
            </div>
            <div className="px-4 py-3 bg-gray-100 rounded-b-2xl flex justify-end">
              <a
                href={doc.fileUrl}
                download
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
              >
                Download
              </a>
            </div>
          </div>
        ))
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-100 transition"
            aria-label="Página anterior"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((item, idx) =>
              item === "..." ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
              ) : (
                <button
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium border transition ${
                    item === safePage
                      ? "bg-blue-900 text-white border-blue-900"
                      : "border-gray-300 hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {item}
                </button>
              )
            )}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            className="p-2 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-100 transition"
            aria-label="Próxima página"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Documents;
